import type { Canvas } from 'fabric';
import type { SetStateAction } from 'react';
import { type MutableRefObject, useCallback } from 'react';
import type { SelectedObjectState } from '../store/atoms/editorSelectionAtom';
import type { ActiveDevice, ActiveLanguage, EditorUiState } from '../store/atoms/editorUiAtom';
import type { SlideData, VariantKey } from '../store/atoms/slideAtom';

export interface UseVariantSwitchParams {
  readonly desktopCanvasRef: MutableRefObject<Canvas | null>;
  readonly mobileCanvasRef: MutableRefObject<Canvas | null>;
  readonly variantKey: VariantKey;
  readonly activeDevice: ActiveDevice;
  readonly activeLanguage: ActiveLanguage;
  readonly slideRef: MutableRefObject<SlideData>;
  readonly saveCurrentSlide: (key: VariantKey) => void;
  readonly generateThumbnail: (canvas: Canvas) => string | undefined;
  readonly setUiState: (update: SetStateAction<EditorUiState>) => void;
  readonly setSlide: (update: SetStateAction<SlideData>) => void;
  readonly setSelection: (value: SetStateAction<SelectedObjectState | null>) => void;
  readonly setTranslationError: (value: SetStateAction<string | null>) => void;
  readonly setCanvasLoadVersion: (update: SetStateAction<number>) => void;
  readonly resetHistory: (key: string) => void;
  readonly updateHistoryState: (key: string) => void;
  readonly pushSnapshot: (key: string) => void;
}

/**
 * Troca de device (desktop/mobile) e idioma da variante ativa, carregando o
 * canvas correspondente, sincronizando histórico e auto-copiando o conteúdo de
 * `pt-BR` quando o idioma alvo ainda está vazio.
 *
 * @param params - Refs dos canvas, variante ativa e sub-hooks de sync/histórico
 * @returns `setActiveDevice`, `setActiveLanguage`
 */
export function useVariantSwitch({
  desktopCanvasRef,
  mobileCanvasRef,
  variantKey,
  activeDevice,
  activeLanguage,
  slideRef,
  saveCurrentSlide,
  generateThumbnail,
  setUiState,
  setSlide,
  setSelection,
  setTranslationError,
  setCanvasLoadVersion,
  resetHistory,
  updateHistoryState,
  pushSnapshot,
}: UseVariantSwitchParams) {
  const setActiveDevice = useCallback(
    (device: ActiveDevice) => {
      saveCurrentSlide(variantKey);
      const newKey: VariantKey = `${activeLanguage}-${device}`;

      setUiState(prev => ({ ...prev, activeDevice: device }));
      setSelection(null);

      const targetCanvas =
        device === 'desktop' ? desktopCanvasRef.current : mobileCanvasRef.current;
      if (targetCanvas) {
        const targetVariant = slideRef.current.variants[newKey];
        if (targetVariant?.canvas) {
          void targetCanvas.loadFromJSON(targetVariant.canvas).then(() => {
            // Com vídeo de fundo, o canvas fica transparente para o <video> aparecer atrás.
            targetCanvas.backgroundColor = targetVariant.backgroundVideo
              ? 'transparent'
              : targetVariant.backgroundColor;
            targetCanvas.requestRenderAll();
            setCanvasLoadVersion(v => v + 1);
          });
        } else {
          targetCanvas.clear();
          targetCanvas.backgroundColor = '#141414';
          targetCanvas.requestRenderAll();
        }
        resetHistory(newKey);
        updateHistoryState(newKey);
      } else {
        updateHistoryState(newKey);
      }
    },
    [
      variantKey,
      activeLanguage,
      saveCurrentSlide,
      setUiState,
      setSelection,
      updateHistoryState,
      resetHistory,
      desktopCanvasRef,
      mobileCanvasRef,
      slideRef,
      setCanvasLoadVersion,
    ],
  );

  const setActiveLanguage = useCallback(
    (language: ActiveLanguage) => {
      saveCurrentSlide(variantKey);
      setTranslationError(null);

      const targetKey: VariantKey = `${language}-${activeDevice}`;
      const targetVariant = slideRef.current.variants[targetKey];
      const isTargetEmpty = !targetVariant?.canvas;

      setUiState(prev => ({ ...prev, activeLanguage: language }));
      setSelection(null);

      const canvas =
        activeDevice === 'desktop' ? desktopCanvasRef.current : mobileCanvasRef.current;
      if (canvas) {
        if (targetVariant?.canvas) {
          void canvas.loadFromJSON(targetVariant.canvas).then(() => {
            // Com vídeo de fundo, o canvas fica transparente para o <video> aparecer atrás.
            canvas.backgroundColor = targetVariant.backgroundVideo
              ? 'transparent'
              : targetVariant.backgroundColor;
            canvas.requestRenderAll();
            setCanvasLoadVersion(v => v + 1);
          });
        } else {
          canvas.clear();
          canvas.backgroundColor = '#141414';
          canvas.requestRenderAll();
        }
        resetHistory(targetKey);
        updateHistoryState(targetKey);
      }

      // Auto-copia pt-BR para o idioma alvo quando está vazio
      if (isTargetEmpty && language !== 'pt-BR') {
        const sourceKey: VariantKey = `pt-BR-${activeDevice}`;
        const sourceVariant = slideRef.current.variants[sourceKey];

        if (sourceVariant?.canvas && canvas) {
          const copiedJson = JSON.parse(JSON.stringify(sourceVariant.canvas)) as Record<
            string,
            unknown
          >;
          void canvas.loadFromJSON(copiedJson).then(() => {
            // Com vídeo de fundo, o canvas fica transparente para o <video> aparecer atrás.
            canvas.backgroundColor = sourceVariant.backgroundVideo
              ? 'transparent'
              : sourceVariant.backgroundColor;
            canvas.requestRenderAll();
            setCanvasLoadVersion(v => v + 1);
            const thumbnail = generateThumbnail(canvas);
            const json = canvas.toObject(['data']) as Record<string, unknown>;
            setSlide(prev => ({
              ...prev,
              variants: {
                ...prev.variants,
                [targetKey]: {
                  canvas: json,
                  backgroundColor: sourceVariant.backgroundColor,
                  backgroundImage: sourceVariant.backgroundImage,
                  ...(sourceVariant.backgroundVideo
                    ? { backgroundVideo: sourceVariant.backgroundVideo }
                    : {}),
                  ...(thumbnail ? { thumbnail } : {}),
                },
              },
            }));
            resetHistory(targetKey);
            pushSnapshot(targetKey);
          });
        }
      }

      if (!isTargetEmpty && canvas) {
        pushSnapshot(targetKey);
      }
    },
    [
      variantKey,
      activeDevice,
      saveCurrentSlide,
      setUiState,
      setSelection,
      updateHistoryState,
      resetHistory,
      pushSnapshot,
      setSlide,
      generateThumbnail,
      desktopCanvasRef,
      mobileCanvasRef,
      slideRef,
      setTranslationError,
      setCanvasLoadVersion,
    ],
  );

  return { setActiveDevice, setActiveLanguage } as const;
}
